import { useEffect, useRef, useCallback } from 'react';

interface UploadSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (type: 'pdf' | 'image' | 'camera') => void;
  theme: 'dark' | 'light';
  isDesktop?: boolean;
}

const allOptions = [
  {
    type: 'pdf' as const,
    icon: 'ri-file-pdf-line',
    label: 'Upload PDF',
    desc: 'Select a PDF document',
  },
  {
    type: 'image' as const,
    icon: 'ri-image-line',
    label: 'Upload Images',
    desc: 'JPG, PNG files',
  },
  {
    type: 'camera' as const,
    icon: 'ri-camera-line',
    label: 'Take Photo',
    desc: 'Use camera',
  },
];

export default function UploadSheet({ isOpen, onClose, onSelect, theme, isDesktop = false }: UploadSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === overlayRef.current) {
        onClose();
      }
    },
    [onClose]
  );

  const handleOption = (type: 'pdf' | 'image' | 'camera') => {
    onSelect(type);
    onClose();
  };

  // Lock body scroll when sheet is open
  useEffect(() => {
    if (isOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [isOpen]);

  const isDark = theme === 'dark';

  const options = isDesktop
    ? allOptions.filter((opt) => opt.type !== 'camera')
    : allOptions;

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className={`fixed inset-0 z-[90] flex items-end justify-center transition-all duration-300 ${
        isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      }`}
    >
      {/* Dark backdrop */}
      <div
        className={`absolute inset-0 transition-opacity duration-300 ${
          isOpen ? 'bg-black/40' : 'bg-black/0'
        }`}
      />

      {/* Sheet */}
      <div
        ref={sheetRef}
        className={`relative w-full max-w-[430px] rounded-t-[28px] transition-transform duration-300 ease-out ${
          isOpen ? 'translate-y-0' : 'translate-y-full'
        } ${isDark ? 'bg-[#1C1D21]' : 'bg-white'}`}
        style={{
          transform: isOpen ? 'translateY(0)' : 'translateY(100%)',
          willChange: 'transform',
        }}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div
            className={`w-9 h-1 rounded-full ${
              isDark ? 'bg-white/20' : 'bg-black/10'
            }`}
          />
        </div>

        {/* Title */}
        <div className="px-5 pb-1 pt-1">
          <h3
            className={`text-[15px] font-semibold ${
              isDark ? 'text-[#F5F5F7]' : 'text-neutral-900'
            }`}
          >
            Upload
          </h3>
          <p
            className={`text-[13px] mt-0.5 ${
              isDark ? 'text-[#6E6E73]' : 'text-neutral-400'
            }`}
          >
            {isDesktop ? 'Choose how to upload your work' : 'Choose how to upload your work'}
          </p>
        </div>

        {/* Options */}
        <div className="px-5 py-3 flex flex-col gap-1">
          {options.map((opt) => (
            <button
              key={opt.type}
              onClick={() => handleOption(opt.type)}
              className={`flex items-center gap-4 w-full py-3.5 px-3 rounded-2xl transition-colors duration-150 cursor-pointer ${
                isDark
                  ? 'hover:bg-white/[0.04]'
                  : 'hover:bg-black/[0.03]'
              }`}
              style={{ touchAction: 'manipulation' }}
            >
              <div
                className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  isDark ? 'bg-white/[0.04]' : 'bg-neutral-100'
                }`}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i
                    className={`${opt.icon} text-[18px] ${
                      isDark ? 'text-[#A1A1A6]' : 'text-neutral-600'
                    }`}
                  />
                </div>
              </div>
              <div className="text-left flex-1 min-w-0">
                <p
                  className={`text-[15px] font-semibold ${
                    isDark ? 'text-[#F5F5F7]' : 'text-neutral-900'
                  }`}
                >
                  {opt.label}
                </p>
                <p
                  className={`text-[13px] ${
                    isDark ? 'text-[#6E6E73]' : 'text-neutral-400'
                  }`}
                >
                  {opt.desc}
                </p>
              </div>
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                <i
                  className={`ri-arrow-right-s-line text-lg ${
                    isDark ? 'text-[#48484F]' : 'text-neutral-300'
                  }`}
                />
              </div>
            </button>
          ))}
        </div>

        {/* Cancel */}
        <div className="px-5 pb-6 pt-1">
          <button
            onClick={onClose}
            className={`w-full py-3.5 rounded-2xl text-[15px] font-semibold cursor-pointer transition-colors duration-150 ${
              isDark
                ? 'bg-white/[0.05] text-[#F5F5F7] hover:bg-white/[0.08]'
                : 'bg-neutral-100 text-neutral-900 hover:bg-neutral-200/60'
            }`}
            style={{ touchAction: 'manipulation' }}
          >
            Cancel
          </button>
        </div>

        {/* Safe area padding */}
        <div className="h-[env(safe-area-inset-bottom)]" />
      </div>
    </div>
  );
}